import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER_COLUMN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { APP_SERVICE, AppService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ensureLoaded } from "@cervval.js/common/src/tools/object.tools";
import { AsYouType, CountryCode, PhoneNumber, getExampleNumber, parsePhoneNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import { CjsInput, InputOptions } from "../../components/inputs/input";
import { CjsInputTextEditor, InputTextEditorOptions, defaultInputTextEditorOptions } from "../inputTextEditor/inputTextEditor";
ensureLoaded(CjsInput);

export type PhoneNumberEditorOptions = InputTextEditorOptions & {
    /** 
     * If set to true, enable to type international numbers. 
     * ! Still experimental
     */
    enableInternationalNumber?: boolean
};

export const defaultPhoneNumberEditorOptions: PhoneNumberEditorOptions = {
    ...defaultInputTextEditorOptions
};


/**
 * Class representing a component with an input text
 */
@CustomElement({
    name: "cjs-phone-number-editor",
    template: require("../inputTextEditor/inputTextEditor.html"),
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_COLUMN]
})
export class CjsPhoneNumberEditor extends CjsInputTextEditor {

    /**
     * AppService to retrieve information about the locale of the user
     */
    @Injected(APP_SERVICE)
    private appService!: AppService;

    /**
     * Formatter for the input, from libphonenumber-js
     */
    private _formatter: AsYouType;

    /**
     * Maximal length for the input, based on the example number length
     */
    private _maxLength: number;

    /**
     * Fallback phone number when no examples found
     */
    private TEMPLATE_PHONE_NUMBER = "+xx xx xx xx xx xx xx xx xx";

    /** Attributes options of PhoneNumberEditor */
    @TypedWebComponentOptions(defaultPhoneNumberEditorOptions)
    public override readonly options!: WebComponentOptions<PhoneNumberEditorOptions>;

    constructor(options?: Partial<PhoneNumberEditorOptions>, inputOptions?: Partial<InputOptions<string>>, customElementOptions?: OverrideCustomElementConfig) {
        super(options, inputOptions, customElementOptions);
        this.options.setWithoutRefresh(options);
        // Retrieve phone locale from AppService
        const phoneLocale = this.appService.getLocale().phoneLocale;

        // TODO Below lines should be in refresh in case options or locale are changed
        // Retrieve example mobile phone number if any
        let exampleCode = "";
        if (this.options.enableInternationalNumber) {
            exampleCode = getExampleNumber(phoneLocale as CountryCode, examples)?.formatInternational() ?? this.TEMPLATE_PHONE_NUMBER;
        } else {
            exampleCode = getExampleNumber(phoneLocale as CountryCode, examples)?.formatNational() ?? this.TEMPLATE_PHONE_NUMBER;
        }
        this._maxLength = exampleCode.length;
        // Set exampleCode as default placeholder
        this.inputOptions.setWithoutRefresh({ placeholderText: exampleCode, ...inputOptions });
        // Init formatter
        this._formatter = new AsYouType(phoneLocale as CountryCode);
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        super.init();

        this.getInput().inputEvent.on(() => {
            // Retrieve value of input, format it and set _value and input value
            this._value = this._formatValue(this.getInput().value ?? "");
            this.getInput().value = this._value;
        });

    }

    /** Refresh component display */
    public override refresh(): void {

        // Force input type tel
        this.inputOptions.setWithoutRefresh({ inputType: "tel" });

        // Refresh
        super.refresh();

        // Set options
        this.inputOptions.setWithoutRefresh({ value: this._value });

        // Always refresh input
        this.getInput().refresh();
    }

    /**
     * Update the input value from stored value
     */
    protected override _refreshInputValue(): void {
        let phoneNumber: PhoneNumber | undefined;
        const phoneLocale = this.appService.getLocale().phoneLocale;
        try {
            phoneNumber = parsePhoneNumber(this._value ?? "", phoneLocale as CountryCode);
        } catch (e) {
            phoneNumber = undefined;
        }
        // If the _phoneLocale is equal to the formatted number country, display it as a national number (ex : 06 12 34 56 78)
        if (phoneNumber) {
            if (phoneLocale === phoneNumber.country) {
                this._value = phoneNumber.formatNational();
            } else {// Else display it as an international number (ex : +33 6 12 34 56 78)
                this._value = phoneNumber.formatInternational();
            }
        }
        this.inputOptions.setAndRefreshIfChanged({ value: this._value });
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected override _getValueFromInputComponent(): string | undefined {
        // ! Bypass default behavior to erase this._value with a correct formatted value
        return this._formatter.getNumberValue();
    }

    /**
     * Return a formatted value for phone input
     * @param value String to format
     * @returns Formatted string
     */
    protected _formatValue(value: string): string {
        // We don't want any + in the value for locale number
        if (!this.options.enableInternationalNumber) {
            value = value.replace("+", "");
        }
        if (value.length > this._maxLength) {
            return value.slice(0, this._maxLength);
        }
        // We reset formatter in case we erased input
        this._formatter.reset();
        // return result of the formatter
        return this._formatter.input(value);
    }
}